import { i18next } from '@weavedev/lit-i18next';
import { ParsedQs } from 'qs';

import { SelectModeEnum } from '@/api-sdk';
import { GTMEventType } from '@/types';

/**
 * Sends a GTM event with the provided event type and data
 * @param eventType The type of GTM event to send
 * @param eventData Additional data to include with the event
 */
export function sendGTMEvent(
  eventType: GTMEventType,
  eventData: Record<string, any> = {},
) {
  window.dispatchEvent(
    new CustomEvent('datalayer-event', {
      detail: { event: eventType, ...eventData },
    }),
  );
}

/**
 * Sends a GTM event for product quantity changes in the configurator
 */
export function sendQuantityChangedEvent() {
  sendGTMEvent(GTMEventType.PRODUCT_CONFIGURATOR, {
    configurator_step_name: 'product_quantity',
    configurator_step: '4',
  });
}

/**
 * Sends a GTM event for color select mode changes
 * Only sends the event if the color select mode has actually changed and not on initial load
 *
 * @param currentColorSelectMode The current color select mode
 * @param newColorSelectMode The new color select mode
 * @returns true if an event was sent, false otherwise
 */
export function sendColorSelectModeChangeEvent(
  currentColorSelectMode: SelectModeEnum | null,
  newColorSelectMode: SelectModeEnum,
): boolean {
  if (
    (currentColorSelectMode !== newColorSelectMode &&
      currentColorSelectMode !== null) ||
    (currentColorSelectMode === null && newColorSelectMode === 'multi')
  ) {
    sendGTMEvent(GTMEventType.PRODUCT_CONFIGURATOR, {
      configurator_step_name: 'product_selection',
      configurator_step: '1',
      interaction_input:
        newColorSelectMode === SelectModeEnum.multi ? 'on' : 'off',
    });
    return true;
  }
  return false;
}

/**
 * Sends a GTM event for color selection changes
 *
 * @param newColors The new array of selected colors
 * @param switchColorCount The number of times the user has switched colors
 */
export function sendColorSelectionChangeEvent(
  newColors: string[],
  switchColorCount: number,
) {
  sendGTMEvent(GTMEventType.PRODUCT_CONFIGURATOR, {
    configurator_step_name: 'product_selection',
    configurator_step: '1',
    product_selection_count: switchColorCount,
    products_selected: newColors.length,
  });
}

/**
 * Sends a GTM event for version selection changes
 * @param version The selected version
 */
export function sendVersionChangedEvent(version: string) {
  sendGTMEvent(GTMEventType.PRODUCT_CONFIGURATOR, {
    configurator_step_name: 'product_version',
    configurator_step: '2',
    interaction_input: version,
  });
}

/**
 * Normalizes color input to an array of strings
 * Handles single strings, arrays, or ParsedQs objects from query parameters
 *
 * @param colors The color input - can be a string, ParsedQs object, or array of either
 * @returns Array of color strings, empty array if no colors provided
 */
export function normalizeColors(
  colors: string | ParsedQs | (string | ParsedQs)[],
) {
  return Array.isArray(colors)
    ? colors.map(color => String(color))
    : colors
      ? [String(colors)]
      : [];
}

/**
 * Sends a GTM event for print option changes
 * Only sends the event if the print option has actually changed
 *
 * @param currentPrintOptionCode The current print option code
 * @param newPrintOptionCode The new print option code
 * @param filterOptionLayout The layout of the filter option
 * @param printOptionCount The number of print options selected
 */
export function sendPrintOptionChangeEvent(
  currentPrintOptionCode: string | null,
  newPrintOptionCode: string,
  filterOptionLayout: string | undefined,
  printOptionCount: number,
) {
  if (currentPrintOptionCode !== newPrintOptionCode) {
    sendGTMEvent(GTMEventType.PRODUCT_CONFIGURATOR, {
      configurator_step_name: 'product_print',
      configurator_step: '3',
      configurator_sub_step: printOptionCount.toString(),
      configurator_sub_step_name: filterOptionLayout,
      interaction_input: newPrintOptionCode,
    });
  }
}

/**
 * Sends a GTM event for the "no print" option
 *
 * @param filterOptionLayout The layout of the filter option
 * @param printOptionCount The number of print options selected
 */
export function sendPrintOptionNoPrintEvent(
  filterOptionLayout: string | undefined,
  printOptionCount: number,
) {
  const en = i18next.getFixedT('en');
  const noPrintText = en('actions.noPrint');
  sendGTMEvent(GTMEventType.PRODUCT_CONFIGURATOR, {
    configurator_step_name: 'product_print',
    configurator_step: '3',
    configurator_sub_step: printOptionCount.toString(),
    configurator_sub_step_name: filterOptionLayout,
    interaction_input: noPrintText,
  });
}

/**
 * Compares two arrays of colors to determine if they've changed
 * @param oldColors The previous array of colors
 * @param newColors The new array of colors
 * @returns true if the colors have changed, false otherwise
 */
export function haveColorsChanged(
  oldColors: string[],
  newColors: string[],
): boolean {
  if (oldColors.length !== newColors.length) {
    return true;
  }

  for (const color of newColors) {
    if (!oldColors.includes(color)) {
      return true;
    }
  }

  for (const color of oldColors) {
    if (!newColors.includes(color)) {
      return true;
    }
  }

  return false;
}
